import { Typography } from '@mui/material'
import { CirclePlusMinor } from '@shopify/polaris-icons'
import { Formik } from 'formik'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { BasicModalDescription, BasicModalHeader } from 'src/components/shared/modal'
import { TableMarketSelect } from 'src/components/shared/table/TableMarketSelect'
import { CreateOpportunity } from 'src/constants/actionTypes'
import { Opportunity } from 'src/interfaces/opportunity'
import { colors } from 'src/theme'
import { handleApiError } from 'src/utils/errorHandlers'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { showToast } from 'src/utils/toast'
import { CreateOpportunityFormValues } from './CreateOpportunityForm.interface'
import { CreateOpportunitySchema } from './CreateOpportunityForm.validator'

interface Props {
  onClose(): void
  onCreated(opportunity: Opportunity): void
}

const initialValues: CreateOpportunityFormValues = {
  title: '',
  description: '',
  partnerMarketIds: [],
}

export const CreateOpportunityForm = ({ onClose, onCreated }: Props) => {
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleFormSubmit = async (values: CreateOpportunityFormValues) => {
    setIsLoading(true)

    try {
      const opportunity: Opportunity = await createAsyncAction(
        dispatch,
        CreateOpportunity.request({
          title: values.title.trim(),
          description: values.description.trim(),
          partnerMarketIds: values.partnerMarketIds,
        }),
      )

      showToast('Opportunity created', { variant: 'success' })

      onCreated(opportunity)
    } catch (err: any) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      validationSchema={CreateOpportunitySchema}
    >
      {({ values, errors, touched, handleSubmit, setFieldValue, setFieldTouched }) => {
        const marketsError = touched.partnerMarketIds ? (errors.partnerMarketIds as string) : ''

        return (
          <div css={tw`max-w-[528px]`} data-testid="opportunities-create-modal">
            <BasicModalHeader
              title="New opportunity"
              onClose={onClose}
              closeButtonDataTestId="opportunities-create-modal-close-btn"
            />
            <BasicModalDescription
              title="Create a shareable opportunity"
              icon={<CirclePlusMinor width={20} height={20} />}
              description="Share the link anywhere you advertise. Applications land in your candidate pool, and nothing starts onboarding until you make an offer."
            />

            <div css={tw`p-4`}>
              <FormikTextField
                name="title"
                css={tw`w-full mb-4`}
                label="Title"
                placeholder="e.g. Cargo Van Delivery Driver"
                inputProps={{ 'data-testid': 'opportunities-create-title' }}
              />

              {/* TableMarketSelect is a trigger-plus-menu, not a form input, so it is wired into Formik by hand.
                  Marked touched on change so its error appears the same way a field's would. */}
              <div css={tw`mb-4`}>
                <Typography variant="h6" css={tw`mb-1`} color={colors.SHADES_GREY_400}>
                  Markets
                </Typography>
                <TableMarketSelect
                  selectedMarketIds={values.partnerMarketIds}
                  setSelectedMarketIds={(marketIds) => {
                    setFieldTouched('partnerMarketIds', true)
                    setFieldValue('partnerMarketIds', marketIds)
                  }}
                  title={
                    values.partnerMarketIds.length
                      ? `${values.partnerMarketIds.length} market${
                          values.partnerMarketIds.length === 1 ? '' : 's'
                        } selected`
                      : 'Select markets'
                  }
                  triggerDataTestId="opportunities-create-markets-trigger"
                  clearAllDataTestId="opportunities-create-markets-clear"
                  searchInputDataTestId="opportunities-create-markets-search"
                />
                <Typography
                  variant="body2"
                  color={marketsError ? colors.COLORS_RED_500 : colors.SHADES_GREY_400}
                  css={tw`mt-1`}
                >
                  {marketsError ||
                    'Pick at least one. Choose more than one and the candidate selects their market on the application form.'}
                </Typography>
              </div>

              <FormikTextField
                name="description"
                css={tw`w-full`}
                label="Description"
                placeholder="Schedule, routes, vehicle requirements — what a candidate needs to know before applying."
                multiline
                minRows={4}
                inputProps={{ 'data-testid': 'opportunities-create-description' }}
              />
            </div>

            <div
              css={tw`flex justify-between items-center px-4 py-3 border-0 border-t border-solid border-[#EDEDED]`}
            >
              <Button
                className={tw`px-8`}
                size="small"
                color="grey"
                disabled={isLoading}
                onClick={onClose}
              >
                Cancel
              </Button>
              {/* Deliberately never disabled: clicking an incomplete form must surface which fields are missing
                  rather than leaving a dead button with no explanation. */}
              <Button
                className={tw`px-8`}
                color="dark"
                size="small"
                loading={isLoading}
                data-testid="opportunities-create-submit"
                onClick={() => {
                  handleSubmit()
                }}
              >
                Create and get link
              </Button>
            </div>
          </div>
        )
      }}
    </Formik>
  )
}
